// Pure helpers for Image Edit. Kept outside React so critical source/job scope
// behavior can be regression-tested without mounting the workspace.

export function clipNameCandidates(clip) {
  return [clip?.original_filename, clip?.name]
    .filter(Boolean)
    .map((value) => String(value).trim());
}

export function matchShotIdForClip(clip, shots) {
  const candidates = clipNameCandidates(clip).map((name) => name.toLowerCase());
  if (!candidates.length) return null;
  const matches = (shots || []).filter((shot) => {
    const sid = String(shot?.shot_id || "").trim().toLowerCase();
    if (!sid) return false;
    return candidates.some((name) => {
      const stem = name.replace(/\.[a-z0-9]{2,5}$/i, "");
      if (stem === sid || name === sid) return true;
      const escaped = sid.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i").test(name);
    });
  });
  return matches.length === 1 ? matches[0].shot_id : null;
}

/**
 * SOURCE AUTHORITY — the pixels the operator selected must win.
 *
 * CreateJobIn currently counts `source_library_asset_id` and `project_id +
 * shot_id` as TWO mutually exclusive source selectors. It is tempting to drop
 * the library id when a filename maps to a shot, but that silently changes the
 * pixels: the project-shot path reads `keyframe_pair`, whose FIRST may already
 * be an edited frame or even the PREVIOUS shot's approved LAST continuity
 * anchor. The operator clicked a concrete ingested clip; opening different
 * pixels is worse than temporarily losing approval scope.
 *
 * `_orca_shot_scope_hint` is deliberately an EXTRA field: the legacy Pydantic
 * model ignores it, while the browser hardening interceptor uses it after the
 * job exists to call `/bind-shot`. The backend then accepts the binding ONLY
 * when the persisted staging/plate link proves that this exact asset belongs to
 * that shot. A filename match proposes; the server authorizes.
 */
export function buildImageEditCreateRequest({ clip, projectId, shotId, frame = "first" } = {}) {
  const titleBase = clip?.original_filename || clip?.name || shotId || "Image Edit";
  const title = `${titleBase} · ${String(frame).toUpperCase()}`;
  if (clip?.id) {
    return {
      source_library_asset_id: clip.id,
      project_id: projectId || clip.project_id || null,
      frame,
      title,
      ...(shotId ? { _orca_shot_scope_hint: shotId } : {}),
    };
  }
  // No guessed fallback. A shot-scoped source must be explicitly selected by a
  // surface that actually owns project+shot semantics.
  return null;
}

export function hydrateImageEditDraft(job) {
  const recipe = job?.last_precision_recipe || {};
  const visual = job?.visual_plan || {};
  return {
    // PrecisionEdit stores the fully compiled prompt for audit/replay. Artist
    // Mode stores the human CHANGE separately so reopening the job never dumps
    // the compiler footer back into the textarea.
    prompt: visual.change_prompt || recipe.prompt || "",
    annotations: Array.isArray(recipe.annotations) ? recipe.annotations : [],
    approvalFrame: job?.approval?.as_frame || job?.source_frame || "first",
    continuityState: job?.approval?.continuity_state || job?.continuity_in?.state || {},
  };
}

/**
 * FAIL-CLOSED para la propagación en vivo.
 *
 * Vive AQUÍ y no en `imageEditRuntime.js` por una razón de gate: el job de CI
 * ejecuta los `*.test.mjs` con `node` ANTES del `npm ci`, a propósito, para que
 * sigan dando señal aunque el árbol de node_modules esté roto. Un test que
 * importase el runtime arrastraría `axios` y explotaría ahí con
 * ERR_MODULE_NOT_FOUND. Este módulo es puro, así que el contrato se puede
 * EJERCITAR en vez de grepear.
 *
 * El backend declara `dry_run: bool = False` — no mandar el campo significa
 * LIVE. Por eso la pregunta no es "¿pidió live?" sino "¿pidió dry-run
 * explícito?": cualquier otra cosa (undefined, null, "false", 0) se bloquea.
 */
export function propagationIsBlocked(body) {
  return (body || {}).dry_run !== true;
}
